/**
 * Course Data
 * Static data for CPR Training Direct courses
 */

import { Course } from '../types/course';

export const courses: Course[] = [
  {
    id: 'cpr-aed',
    slug: 'cpr-aed',
    name: 'CPR & AED Certification',
    tagline: 'Learn life-saving skills in just 4 hours',
    icon: '🫀',
    description:
      'Comprehensive CPR and AED training for adults, children, and infants. Perfect for teachers, coaches, parents, childcare providers, and anyone wanting to be prepared for emergencies. Receive same-day certification valid for 2 years.',
    shortDescription:
      'Complete CPR and AED training for all ages with same-day certification.',
    whatYouLearn: [
      'Adult, child, and infant CPR techniques',
      'Proper use of Automated External Defibrillator (AED)',
      'Choking relief for all age groups',
      'Recognition of cardiac arrest and breathing emergencies',
      'Hands-only CPR for public responders',
      'Two-rescuer CPR and team response',
    ],
    duration: '3-4 hours',
    certification: 'AHA',
    certificationValidity: '2 years',
    formats: ['in-person', 'blended'],
    prerequisites: null,
    pricing: [
      {
        type: 'individual',
        amount: 85,
        details: 'Walk-in registration',
      },
      {
        type: 'early-bird',
        amount: 75,
        details: 'Register 7+ days in advance',
      },
      {
        type: 'group',
        amount: 70,
        details: 'Groups of 5 or more',
      },
    ],
    locations: [
      { city: 'Orlando', state: 'FL', available: true },
      { city: 'Atlanta', state: 'GA', available: true },
    ],
    whatsIncluded: [
      'Same-day digital certification card',
      'Physical certification card (mailed within 5 business days)',
      'Training materials and course manual',
      'Hands-on practice with professional mannequins',
      'Access to online refresher resources',
      'Emergency reference guide',
    ],
    whoShouldTake:
      'Teachers, coaches, parents, caregivers, childcare providers, fitness instructors, and anyone wanting to be prepared for emergencies.',
    featured: true,
    order: 1,
  },
  {
    id: 'cpr-renewal',
    slug: 'cpr-renewal',
    name: 'CPR Renewal / Recertification',
    tagline: 'Renew your certification quickly and conveniently',
    icon: '🔄',
    description:
      'Fast and efficient CPR recertification for those with current or recently expired certifications. Review critical skills, practice techniques, and maintain your certification status. Most renewals completed in 2-3 hours.',
    shortDescription:
      'Quick CPR recertification with updated guidelines and techniques.',
    whatYouLearn: [
      'Updated CPR guidelines and techniques',
      'AED refresher training',
      'Recognition of emergency situations',
      'Skill validation and assessment',
      'Latest American Heart Association protocols',
    ],
    duration: '2-3 hours',
    certification: 'AHA',
    certificationValidity: '2 years',
    formats: ['in-person', 'blended', 'online'],
    prerequisites: 'Previous CPR certification (can be expired within 1 year)',
    pricing: [
      {
        type: 'individual',
        amount: 65,
        details: 'Walk-in registration',
      },
      {
        type: 'early-bird',
        amount: 55,
        details: 'Register 7+ days in advance',
      },
      {
        type: 'group',
        amount: 50,
        details: 'Groups of 5 or more',
      },
    ],
    locations: [
      { city: 'Orlando', state: 'FL', available: true },
      { city: 'Atlanta', state: 'GA', available: true },
    ],
    whatsIncluded: [
      'Same-day digital certification card',
      'Physical certification card (mailed)',
      'Updated reference materials',
      'Skills practice and assessment',
      'New 2-year certification',
    ],
    whoShouldTake:
      'Healthcare professionals, teachers, employees with expiring certifications, and anyone needing recertification.',
    featured: true,
    order: 2,
  },
  {
    id: 'bls',
    slug: 'bls',
    name: 'BLS for Healthcare Providers',
    tagline: 'Advanced life support training for medical professionals',
    icon: '⚕️',
    description:
      'Comprehensive Basic Life Support training designed specifically for healthcare professionals. Covers advanced CPR techniques, team dynamics, and critical decision-making in medical emergencies. Meets requirements for nurses, doctors, EMTs, and medical staff.',
    shortDescription:
      'Professional-level BLS training for healthcare workers with AHA certification.',
    whatYouLearn: [
      'High-quality CPR for adults, children, and infants',
      'Advanced AED operation and troubleshooting',
      'Bag-valve-mask ventilation techniques',
      'Opioid overdose response and naloxone administration',
      'Effective team communication during resuscitation',
      'Relief of foreign body airway obstruction',
      'Recognition and early management of respiratory and cardiac arrest',
    ],
    duration: '4-5 hours',
    certification: 'AHA',
    certificationValidity: '2 years',
    formats: ['in-person', 'blended'],
    prerequisites: 'Recommended for healthcare professionals or those entering the medical field',
    pricing: [
      {
        type: 'individual',
        amount: 95,
        details: 'Walk-in registration',
      },
      {
        type: 'early-bird',
        amount: 85,
        details: 'Register 7+ days in advance',
      },
      {
        type: 'group',
        amount: 80,
        details: 'Groups of 5 or more',
      },
    ],
    locations: [
      { city: 'Orlando', state: 'FL', available: true },
      { city: 'Atlanta', state: 'GA', available: true },
    ],
    whatsIncluded: [
      'American Heart Association BLS Provider certification card',
      'BLS Provider Manual and reference materials',
      'Hands-on practice with medical-grade equipment',
      'Team scenario training',
      'Digital certification card (same day)',
      'Physical card (mailed within 5 business days)',
    ],
    whoShouldTake:
      'Registered nurses, EMTs, paramedics, physicians, physician assistants, medical assistants, dental professionals, and medical students.',
    featured: true,
    order: 3,
  },
  {
    id: 'group-training',
    slug: 'group-training',
    name: 'Group & On-Site Training',
    tagline: 'Convenient training at your location for teams and organizations',
    icon: '👥',
    description:
      'Bring professional CPR and first aid training directly to your workplace, school, or facility. Perfect for businesses, healthcare facilities, schools, daycares, and organizations that need to certify multiple staff members. Flexible scheduling and customized training options available.',
    shortDescription:
      'Mobile CPR training for businesses and organizations at your location.',
    whatYouLearn: [
      "Customized training based on your organization's needs",
      'All standard CPR, AED, and first aid techniques',
      'Workplace-specific emergency scenarios',
      'Team coordination and emergency response planning',
      'Optional add-ons: First Aid, Bloodborne Pathogens, AED training',
    ],
    duration: 'Varies by group size and course type',
    certification: 'AHA',
    certificationValidity: '2 years',
    formats: ['on-site'],
    prerequisites: 'Minimum 10 participants for on-site training',
    pricing: [
      {
        type: 'individual',
        amount: 70,
        details: 'Per person for groups of 10-20',
      },
      {
        type: 'group',
        amount: 60,
        details: 'Per person for groups of 20+',
      },
      {
        type: 'early-bird',
        amount: 65,
        details: 'Book 14+ days in advance (10-20 people)',
      },
    ],
    locations: [
      { city: 'Orlando', state: 'FL', available: true },
      { city: 'Atlanta', state: 'GA', available: true },
      { city: 'On-Site', state: 'Any', available: true },
    ],
    whatsIncluded: [
      'Professional instructor travel to your location',
      'All training equipment and mannequins provided',
      'Individual certification cards for all participants',
      'Course materials and reference guides',
      'Flexible scheduling (evenings and weekends available)',
      'Free consultation to determine training needs',
      'Certificate of completion for organizational records',
    ],
    whoShouldTake:
      'Corporate teams, healthcare facilities, schools, daycare centers, fitness centers, construction sites, churches, restaurants, and hospitality businesses.',
    featured: false,
    order: 4,
  },
];

/**
 * Get course by slug
 */
export const getCourseBySlug = (slug: string): Course | undefined => {
  return courses.find((course) => course.slug === slug);
};

/**
 * Get featured courses
 */
export const getFeaturedCourses = (): Course[] => {
  return courses.filter((course) => course.featured);
};

/**
 * Get all courses sorted by order
 */
export const getAllCourses = (): Course[] => {
  return [...courses].sort((a, b) => a.order - b.order);
};
